import React from 'react';
import { Redirect } from '@shared/core';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { ExistingDraftsWarningProvider } from '@apps/card/components/ExistingDraftsWarning';
import { RestrictedInternationalShippingWarningProvider } from '@apps/card/components/RestrictedInternationalShippingWarning';
import type { CustomizeEnvelopeTemplate } from '@apps/card/types/stationeryTemplate.types';
import { PdpLayout } from '@apps/card/routes/Dashboard/components/ProductDetailsPage/components/PdpLayout/PdpLayout';
import type { DraftJson } from '@apps/card/routes/CardCustomizer/DraftJson.types';
import { EnvelopePdp } from './EnvelopePdp';
import { useStartEnvelopeOrder } from './useEnvelopeEntry';
import { useEnvelopeFallbackPath } from './useEnvelopeFallbackPath';

export type EnvelopesProps = Readonly<{
  eventId?: string;
  /**
   * What to do when the visitor has no session or no event. Supplied only by the public mount,
   * which sits inside `PublicShopLoginProvider` and can therefore open the sign-in modal; the admin mount
   * passes none, because reaching it at all implies both. Mirrors `ShopRouter`'s
   * `onCustomizeClickCardOverride`.
   */
  onUnauthenticated?: (data: CustomizeEnvelopeTemplate & { priceConfigId?: string }) => void;
}>;

/**
 * Renders the envelope PDP and starts the order from its Customize Now button.
 *
 * Creation used to fire on mount here, with a `JoyLogoLoader` as the whole page. That turned out
 * to hide the restricted-shipping and existing-drafts dialogs completely: `PageLoaderOverlay` sits
 * at `z-index: 1402` against their 1400 and is `pointer-events: none`, so the route looked like an
 * infinite spinner while actually waiting on an answer nobody could see. A real page underneath
 * removes the full-page loader the guards were hiding behind, and gives the shopper somewhere to
 * stand when one of them is dismissed.
 */
const EnvelopesEntry = ({ onUnauthenticated }: Pick<EnvelopesProps, 'onUnauthenticated'>) => {
  const { carrier, product, startEnvelopeOrder, isLoading, isResolved, isCreating, canStart, canOfferSignIn } = useStartEnvelopeOrder(onUnauthenticated);
  const fallbackPath = useEnvelopeFallbackPath();

  if (isLoading) {
    return <PdpLayout.Skeleton />;
  }

  // Resolved but unstartable means the carrier row or the product is missing from this
  // environment — the carrier is inserted by hand and nothing recreates it, so this is a real
  // failure mode rather than a theoretical one. Send them somewhere useful instead of hanging.
  if (!isResolved || !carrier || !product) {
    return <Redirect to={fallbackPath} />;
  }

  // Unauthenticated on a mount that cannot offer sign-in — the admin path, reachable only by a
  // stale or hand-typed URL. Bounce rather than render a page whose only button cannot work; the
  // public mount offers sign-in from the button instead.
  if (!canStart && !canOfferSignIn) {
    return <Redirect to={fallbackPath} />;
  }

  // Note what is deliberately NOT here: a redirect when creation stops. A dismissed guard dialog
  // or a failed mutation leaves the shopper on this page with the CTA re-enabled, because the page
  // is now a useful place to be — unlike the bare loader this route used to render, which had to
  // redirect or strand them.
  return (
    <EnvelopePdp
      carrierId={carrier.id}
      carrierThemeId={carrier.themeId}
      carrierJson={carrier.themeJSON as DraftJson}
      product={product}
      isCreatingDraft={isCreating}
      onCustomizeClick={startEnvelopeOrder}
    />
  );
};

/**
 * `/cards/envelopes` — the standalone-envelope entry point, mounted by both the admin dashboard
 * and the public shop so the gallery tile works wherever it is shown.
 *
 * Gated independently of that tile. Gating only the tile would leave the route reachable by a
 * typed or shared URL, and the page it renders mutates: its button creates a draft.
 *
 * The gate waits for `isLoading` before deciding. `useIsFeatureEnabled` resolves a remote flag as
 * `value === 'on'`, so it reads false while the value is still in flight — redirecting on
 * `!isEnabled` alone would bounce a legitimate visitor mid-resolve. Reading false is otherwise the
 * safe direction here: an unregistered or unresolved flag hides the feature rather than exposing
 * it, which is the opposite of the race PRNT-5293 had to fix in the customizer's step guard.
 */
export const Envelopes = ({ eventId, onUnauthenticated }: EnvelopesProps) => {
  const [isEnvelopeProductEnabled, isFlagLoading] = useIsFeatureEnabled('printEnvelopeProduct');
  const fallbackPath = useEnvelopeFallbackPath();

  if (isFlagLoading) {
    return <PdpLayout.Skeleton />;
  }

  if (!isEnvelopeProductEnabled) {
    return <Redirect to={fallbackPath} />;
  }

  return (
    <RestrictedInternationalShippingWarningProvider eventId={eventId}>
      <ExistingDraftsWarningProvider eventId={eventId}>
        <EnvelopesEntry onUnauthenticated={onUnauthenticated} />
      </ExistingDraftsWarningProvider>
    </RestrictedInternationalShippingWarningProvider>
  );
};
